import { useState } from 'react';

const strategies = [
  {
    id: 'value',
    icon: 'ri-search-eye-line',
    title: 'Apuestas de Valor (Value Betting)',
    tag: 'Fundamental',
    tagColor: '#00e87a',
    summary: 'La estrategia más importante. Consiste en identificar cuotas que el bookmaker ha fijado por encima de la probabilidad real del evento.',
    content: [
      {
        subtitle: '¿Qué es el valor esperado?',
        text: 'Una apuesta tiene valor positivo cuando la probabilidad real del evento es mayor que la probabilidad implícita en la cuota. Si crees que un equipo tiene un 60% de probabilidades de ganar pero la cuota implica solo un 50%, tienes una apuesta de valor.',
      },
      {
        subtitle: 'Fórmula del Valor Esperado (EV)',
        text: 'EV = (Probabilidad estimada × Ganancia neta) - (Probabilidad de perder × Stake)\n\nEjemplo: Cuota 2.20 en un evento con probabilidad real del 55%\nEV = (0.55 × 1.20) - (0.45 × 1) = 0.66 - 0.45 = +0.21 → Apuesta con valor',
      },
      {
        subtitle: 'Cómo encontrar valor',
        text: 'Compara cuotas entre múltiples casas. Especialízate en ligas o mercados específicos donde tengas más conocimiento que el mercado. Usa estadísticas avanzadas para estimar probabilidades propias.',
      },
    ],
  },
  {
    id: 'arbitrage',
    icon: 'ri-scales-3-line',
    title: 'Arbitraje (Arbing)',
    tag: 'Sin riesgo',
    tagColor: '#06b6d4',
    summary: 'Aprovecha diferencias de cuotas entre casas de apuestas para cubrir todos los resultados posibles y garantizar un beneficio independientemente del resultado.',
    content: [
      {
        subtitle: '¿Cómo funciona?',
        text: 'Si la Casa A ofrece cuota 2.10 para el equipo local y la Casa B ofrece cuota 2.10 para el visitante, puedes apostar en ambas y garantizar beneficio. La suma de las probabilidades implícitas debe ser inferior al 100%.',
      },
      {
        subtitle: 'Cálculo de la apuesta de arbitraje',
        text: 'Porcentaje de arb = (1/cuota1 + 1/cuota2) × 100\nSi el resultado es < 100%, existe arbitraje.\n\nEjemplo: 1/2.10 + 1/2.10 = 0.476 + 0.476 = 95.2% → Beneficio garantizado del 4.8%',
      },
      {
        subtitle: 'Riesgos y limitaciones',
        text: 'Las casas limitan o cierran cuentas de apostadores que hacen arbing sistemático. Las cuotas cambian rápido. Requiere capital en múltiples casas simultáneamente.',
      },
    ],
  },
  {
    id: 'handicap',
    icon: 'ri-bar-chart-grouped-line',
    title: 'Hándicap Asiático',
    tag: 'Intermedio',
    tagColor: '#f59e0b',
    summary: 'Elimina el empate como resultado posible y ofrece cuotas más equilibradas. Muy popular en fútbol y con menor margen de la casa que los mercados tradicionales.',
    content: [
      {
        subtitle: '¿Qué es el hándicap asiático?',
        text: 'Se asigna una ventaja o desventaja de goles a cada equipo antes del partido. Si apuestas al favorito con -1.5, necesita ganar por 2 o más goles. Si apuestas al underdog con +1.5, ganas si empata o pierde por 1.',
      },
      {
        subtitle: 'Hándicap de cuarto de gol',
        text: 'Los hándicaps de 0.25 y 0.75 dividen la apuesta en dos partes iguales. Con -0.25: si gana cobras todo, si empata pierdes la mitad. Esto reduce el riesgo y permite posiciones más matizadas.',
      },
      {
        subtitle: 'Ventajas sobre 1X2',
        text: 'Menor margen de la casa (típicamente 2-3% vs 5-8% en 1X2). Cuotas más cercanas a 2.00 facilitan el cálculo de valor. Mercado más eficiente y líquido.',
      },
    ],
  },
  {
    id: 'stats',
    icon: 'ri-bar-chart-2-line',
    title: 'Análisis Estadístico',
    tag: 'Avanzado',
    tagColor: '#8b5cf6',
    summary: 'Usa métricas avanzadas como xG, PPDA, presión alta y datos de rendimiento para identificar equipos infravalorados o sobrevalorados por el mercado.',
    content: [
      {
        subtitle: 'Expected Goals (xG)',
        text: 'El xG mide la calidad de las ocasiones de gol, no solo el resultado. Un equipo con xG 2.1 que perdió 0-1 probablemente fue el mejor equipo. Busca equipos con xG consistentemente superior a sus resultados reales.',
      },
      {
        subtitle: 'PPDA y presión alta',
        text: 'PPDA (Passes Allowed Per Defensive Action) mide la intensidad de la presión. Equipos con PPDA bajo presionan más alto. Útil para predecir partidos donde un equipo dominará la posesión y creará más ocasiones.',
      },
      {
        subtitle: 'Regresión a la media',
        text: 'Los equipos con muchos goles de córner o faltas tienden a regresar a la media. Un equipo con 8 victorias seguidas probablemente tiene algo de suerte. Identifica cuándo el mercado sobrevalora rachas recientes.',
      },
    ],
  },
];

export default function StrategiesSection() {
  const [active, setActive] = useState('value');
  const [contentIdx, setContentIdx] = useState(0);
  const selected = strategies.find((s) => s.id === active)!;

  const handleSelect = (id: string) => {
    setActive(id);
    setContentIdx(0);
  };

  return (
    <section id="estrategias" className="py-20 bg-[#0a0c11]">
      <div className="max-w-[1440px] mx-auto px-6">
        {/* Header */}
        <div className="flex items-start gap-4 mb-12">
          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f59e0b]/10 border border-[#f59e0b]/20 flex-shrink-0">
            <i className="ri-brain-line text-[#f59e0b] text-xl"></i>
          </div>
          <div>
            <p className="text-[#f59e0b] text-xs font-semibold uppercase tracking-widest mb-1">Capítulo 2</p>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>
              Estrategias de Apuesta
            </h2>
            <p className="text-[#a8b2c1] text-base max-w-2xl">
              No todas las apuestas son iguales. Conocer las estrategias correctas te permite identificar oportunidades reales y evitar las trampas del mercado.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Strategy selector */}
          <div className="flex flex-col gap-2">
            {strategies.map((s) => (
              <button
                key={s.id}
                onClick={() => handleSelect(s.id)}
                className={`flex flex-col gap-1.5 px-4 py-4 rounded-xl border text-left transition-all cursor-pointer ${
                  active === s.id
                    ? 'bg-[#0d0f14] border-[#2a2d3a]'
                    : 'bg-[#0d0f14] border-[#1e2028] hover:border-[#2a2d3a]'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/5">
                    <i className={`${s.icon} text-sm`} style={{ color: active === s.id ? s.tagColor : '#6b7280' }}></i>
                  </div>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: s.tagColor, backgroundColor: `${s.tagColor}15` }}>
                    {s.tag}
                  </span>
                </div>
                <p className={`text-sm font-semibold leading-tight ${active === s.id ? 'text-white' : 'text-[#a8b2c1]'}`}>{s.title}</p>
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="lg:col-span-3 bg-[#0d0f14] border border-[#1e2028] rounded-xl overflow-hidden">
            {/* Summary */}
            <div className="p-6 border-b border-[#1e2028]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${selected.tagColor}15` }}>
                  <i className={`${selected.icon} text-base`} style={{ color: selected.tagColor }}></i>
                </div>
                <h3 className="text-white font-bold text-lg">{selected.title}</h3>
              </div>
              <p className="text-[#a8b2c1] text-sm leading-relaxed">{selected.summary}</p>
            </div>

            {/* Sub-tabs */}
            <div className="flex border-b border-[#1e2028]">
              {selected.content.map((c, i) => (
                <button
                  key={i}
                  onClick={() => setContentIdx(i)}
                  className={`px-5 py-3 text-xs font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                    contentIdx === i
                      ? 'text-white border-b-2 border-[#00e87a]'
                      : 'text-[#6b7280] hover:text-[#a8b2c1]'
                  }`}
                >
                  {c.subtitle}
                </button>
              ))}
            </div>

            {/* Content body */}
            <div className="p-6">
              <p className="text-[#a8b2c1] text-sm leading-relaxed whitespace-pre-line">
                {selected.content[contentIdx].text}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
